import { FC } from 'react';
import type { KpiLens } from '../forecasting.types';
import type { QuarterTotals } from '../threeMonth.types';
import { FORECASTING_THEME } from '../forecastingTheme';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { KpiCard, KpiGroup, KpiGroupTitle, KpiStat, QuarterKpiRow } from '../Forecasting.styles';

interface ThreeMonthKpiScorecardsProps {
  totals: QuarterTotals;
  activeLens: KpiLens;
  onSelect: (lens: KpiLens) => void;
}

export const ThreeMonthKpiScorecards: FC<ThreeMonthKpiScorecardsProps> = ({
  totals,
  activeLens,
  onSelect
}) => (
  <QuarterKpiRow>
    {/* Revenue only, and not a lens: the chart already shows booked and pipe
        together, and the table breaks them out. */}
    <KpiGroup>
      <KpiGroupTitle>Booked &amp; Pipe</KpiGroupTitle>
      <KpiStat $border={FORECASTING_THEME.booked}>
        <div className="label">Booked</div>
        <div className="value">{formatCurrency(totals.booked)}</div>
      </KpiStat>
      <KpiStat $border={FORECASTING_THEME.booked}>
        <div className="label">Pipe</div>
        <div className="value">{formatCurrency(totals.pipe)}</div>
      </KpiStat>
    </KpiGroup>

    <KpiGroup>
      <KpiGroupTitle>Dynamic Forecast</KpiGroupTitle>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiDynamicBorder}
        $active={activeLens === 'dynamicForecastRevenue'}
        onClick={() => onSelect('dynamicForecastRevenue')}
      >
        <div className="label">Revenue</div>
        <div className="value">{formatCurrency(totals.dynamicRevenue)}</div>
      </KpiCard>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiDynamicBorder}
        $active={activeLens === 'dynamicForecastGp'}
        onClick={() => onSelect('dynamicForecastGp')}
      >
        <div className="label">Gross Profit</div>
        <div className="value">{formatCurrency(totals.dynamicGp)}</div>
      </KpiCard>
    </KpiGroup>

    <KpiGroup>
      <KpiGroupTitle>Target</KpiGroupTitle>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiBudgetBorder}
        $active={activeLens === 'budgetRevenue'}
        onClick={() => onSelect('budgetRevenue')}
      >
        <div className="label">Revenue</div>
        <div className="value">
          {totals.budgetRevenue != null ? formatCurrency(totals.budgetRevenue) : '—'}
        </div>
      </KpiCard>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiBudgetBorder}
        $active={activeLens === 'budgetGp'}
        onClick={() => onSelect('budgetGp')}
      >
        <div className="label">Gross Profit</div>
        <div className="value">
          {totals.budgetGp != null ? formatCurrency(totals.budgetGp) : '—'}
        </div>
      </KpiCard>
    </KpiGroup>

    <KpiGroup>
      <KpiGroupTitle>vs Target</KpiGroupTitle>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiVsBorder}
        $active={activeLens === 'vsBudgetRevenue'}
        onClick={() => onSelect('vsBudgetRevenue')}
      >
        <div className="label">Revenue</div>
        <div className="value">
          {totals.vsBudgetRevenuePct != null ? formatPercent(totals.vsBudgetRevenuePct, 0) : '—'}
        </div>
      </KpiCard>
      <KpiCard
        type="button"
        $border={FORECASTING_THEME.kpiVsBorder}
        $active={activeLens === 'vsBudgetGp'}
        onClick={() => onSelect('vsBudgetGp')}
      >
        <div className="label">Gross Profit</div>
        <div className="value">
          {totals.vsBudgetGpPct != null ? formatPercent(totals.vsBudgetGpPct, 0) : '—'}
        </div>
      </KpiCard>
    </KpiGroup>
  </QuarterKpiRow>
);
